package co.maytes.standupbot.dashboard.pages

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.layout.MarketingShell
import co.maytes.standupbot.dashboard.components.marketing.Brand
import co.maytes.standupbot.dashboard.components.marketing.BrandGlyph
import co.maytes.standupbot.dashboard.components.marketing.Fold
import co.maytes.standupbot.dashboard.components.marketing.MkIcon
import co.maytes.standupbot.dashboard.components.marketing.SectionHead
import co.maytes.standupbot.dashboard.components.marketing.StandupDemo
import co.maytes.standupbot.dashboard.components.marketing.StandupTimeCalculator
import co.maytes.standupbot.dashboard.components.marketing.TaglineReveal
import co.maytes.standupbot.dashboard.components.motion.Reveal
import co.maytes.standupbot.dashboard.styles.Marketing
import co.maytes.standupbot.dashboard.styles.MarketingIcon
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

private const val INSTALL_PATH = "/oauth/slack/install"
private const val INSTALL_LABEL = "Add to Slack, free"
private const val HERO_LEDE_INDEX = 2
private const val HERO_STAGE_INDEX = 5

private const val TAGLINE =
    "No questionnaire. No meeting. Just the update, written from what your team shipped " +
        "and posted to Slack before anyone logs on."

private val heroTitleLines = listOf("Your standup", "is already written.")

@Page
@Composable
fun LandingPage() {
    MarketingShell {
        Div(attrs = { classes(Marketing.PAGE_CLASS) }) {
            HeroSection()
            StageSection()
            TaglineSection()
            CalculatorSection()
            HowItWorksSection()
            ControlSection()
            BenefitsSection()
            PriceSection()
            TrustSection()
            FaqSection()
            ClosingSection()
        }
    }
}

@Composable
private fun Enter(index: Int, content: @Composable () -> Unit) {
    Div(
        attrs = {
            classes(Marketing.ENTER_CLASS)
            style { property(Marketing.INDEX_VAR, "$index") }
        },
    ) { content() }
}

@Composable
private fun ArrowLabel(label: String) {
    Text(label)
    Span(attrs = { classes("mk-btn-arrow"); attr("aria-hidden", "true") }) { Text("→") }
}

@Composable
private fun HeroSection() {
    Section(attrs = { classes("mk-hero") }) {
        Div(attrs = { classes("mk-wrap", "mk-hero-inner") }) {
            H1(attrs = { classes("mk-display") }) {
                heroTitleLines.forEachIndexed { index, line ->
                    Span(
                        attrs = {
                            classes("mk-line", Marketing.ENTER_CLASS)
                            style { property(Marketing.INDEX_VAR, "$index") }
                        },
                    ) { Text(line) }
                }
            }
            Enter(HERO_LEDE_INDEX) {
                P(attrs = { classes("mk-lede") }) {
                    Text(
                        "StandupBot reads yesterday's Linear tickets and GitHub pull requests, " +
                            "writes each person's update with Claude, and posts it to Slack.",
                    )
                }
            }
            Enter(HERO_LEDE_INDEX + 1) {
                Div(attrs = { classes("mk-actions") }) {
                    Link(
                        path = INSTALL_PATH,
                        modifier = Modifier.classNames("mk-btn", "mk-btn--primary"),
                    ) { ArrowLabel(INSTALL_LABEL) }
                }
            }
            Enter(HERO_LEDE_INDEX + 2) {
                P(attrs = { classes("mk-proof") }) {
                    Text("Free for 5 people. After that, $15 a month per workspace with unlimited seats.")
                }
            }
        }
    }
}

@Composable
private fun StageSection() {
    Section(attrs = { classes("mk-stagefield", "mk-anchor"); attr("id", "sample") }) {
        Div(attrs = { classes("mk-wrap") }) {
            Div(
                attrs = {
                    classes("mk-showcase", Marketing.ENTER_CLASS)
                    style { property(Marketing.INDEX_VAR, "$HERO_STAGE_INDEX") }
                },
            ) { StandupDemo() }
        }
    }
}

@Composable
private fun TaglineSection() {
    Section(attrs = { classes("mk-tagline-section") }) {
        Div(attrs = { classes("mk-wrap") }) { TaglineReveal(TAGLINE) }
    }
}

@Composable
private fun CalculatorSection() {
    Section(attrs = { classes("mk-section", "mk-band") }) {
        Div(attrs = { classes("mk-wrap") }) {
            SectionHead(title = "The old way adds up.", line = "Move the sliders to match your team.")
            Reveal { StandupTimeCalculator() }
        }
    }
}

private data class HowStep(val brands: List<Brand>, val title: String, val line: String)

private val howSteps = listOf(
    HowStep(
        listOf(Brand.Linear, Brand.GitHub),
        "Connect your work",
        "Link Linear with one click. Yesterday's tickets and pull requests are pulled in on their own.",
    ),
    HowStep(
        listOf(Brand.Slack),
        "Pick a channel and a time",
        "Choose the team, the days and the hour. Holidays can be skipped.",
    ),
    HowStep(
        listOf(Brand.Claude),
        "Updates write themselves",
        "Claude drafts each person's update and it lands in Slack on schedule.",
    ),
)

@Composable
private fun HowItWorksSection() {
    Section(attrs = { classes("mk-steps-section", "mk-anchor"); attr("id", "pipeline") }) {
        Div(attrs = { classes("mk-wrap") }) {
            SectionHead(title = "Set up once. Done every morning.", line = "Five steps from install to your first standup.")
            Div(attrs = { classes("mk-how") }) {
                howSteps.forEachIndexed { index, step ->
                    Reveal(index = index, modifier = Modifier.classNames("mk-how-item")) {
                        Div(attrs = { classes("mk-logos"); attr("aria-hidden", "true") }) {
                            step.brands.forEach { brand ->
                                Span(attrs = { classes("mk-logo") }) { BrandGlyph(brand) }
                            }
                        }
                        H3(attrs = { classes("mk-how-title") }) { Text(step.title) }
                        P(attrs = { classes("mk-body") }) { Text(step.line) }
                    }
                }
            }
        }
    }
}

private data class Choice(val title: String, val line: String)

private val choices = listOf(
    Choice("Approve", "One click posts the update as written."),
    Choice("Edit", "Fix a line first, and the team sees your version."),
    Choice("Skip", "Out sick or on leave? Skip it and nothing goes out for you."),
)

@Composable
private fun ControlSection() {
    Section(attrs = { classes("mk-section", "mk-band") }) {
        Div(attrs = { classes("mk-wrap") }) {
            SectionHead(
                title = "Nobody gets spoken for.",
                line = "Everyone reviews their own update before it posts.",
            )
            Div(attrs = { classes("mk-how") }) {
                choices.forEachIndexed { index, choice ->
                    Reveal(index = index, modifier = Modifier.classNames("mk-how-item")) {
                        H3(attrs = { classes("mk-how-title") }) { Text(choice.title) }
                        P(attrs = { classes("mk-body") }) { Text(choice.line) }
                    }
                }
            }
        }
    }
}

private data class Benefit(val title: String, val line: String)

private val benefits = listOf(
    Benefit("Summaries for any week.", "Standups add up, so Friday's recap is already written."),
    Benefit("Every token, every cent.", "See what Claude costs your workspace, down to the cent."),
    Benefit("Your hour, your days.", "Pick the days and the time. Skip holidays."),
    Benefit("Your key, locked.", "Bring your own Anthropic key on Pro and Business."),
)

@Composable
private fun BenefitsSection() {
    Section(attrs = { classes("mk-section", "mk-anchor"); attr("id", "features") }) {
        Div(attrs = { classes("mk-wrap") }) {
            SectionHead(title = "More than a morning message.", line = "All of it runs on the same history.")
            Div(attrs = { classes("mk-benefits") }) {
                benefits.forEachIndexed { index, benefit ->
                    Reveal(index = index % 2, modifier = Modifier.classNames("mk-benefit")) {
                        H3(attrs = { classes("mk-benefit-title") }) { Text(benefit.title) }
                        P(attrs = { classes("mk-body") }) { Text(benefit.line) }
                    }
                }
            }
        }
    }
}

@Composable
private fun PriceSection() {
    Section(attrs = { classes("mk-section", "mk-band") }) {
        Div(attrs = { classes("mk-wrap") }) {
            Reveal(modifier = Modifier.classNames("mk-closing-inner", "mk-price-strip")) {
                H2(attrs = { classes("mk-h2") }) { Text("One price per workspace.") }
                P(attrs = { classes("mk-lede") }) {
                    Text(
                        "Free for up to 5 people. Pro is $15 a month billed annually, " +
                            "with unlimited members and standups. Adding a teammate never changes the bill.",
                    )
                }
                Link(path = "/pricing", text = "See all plans →", modifier = Modifier.classNames("mk-quiet"))
            }
        }
    }
}

private val capabilities = listOf(
    "Tokens encrypted with AES GCM",
    "Your Anthropic key on Pro and Business",
    "Hosted in Sydney",
    "Audit log on Pro and Business",
    "CSV export on Business",
    "Free plan, no card needed",
)

@Composable
private fun TrustSection() {
    Section(attrs = { classes("mk-section") }) {
        Div(attrs = { classes("mk-wrap") }) {
            SectionHead(title = "Your data, your key.", line = "Built so you stay in control.")
            Reveal {
                Ul(attrs = { classes("mk-caps") }) {
                    capabilities.forEach { label ->
                        Li(attrs = { classes("mk-cap") }) {
                            MkIcon(MarketingIcon.Check)
                            Span { Text(label) }
                        }
                    }
                }
            }
            Reveal(index = 1, modifier = Modifier.classNames("mk-caps-link")) {
                Link(
                    path = "/docs/anthropic-key",
                    text = "How to create your Anthropic key →",
                    modifier = Modifier.classNames("mk-quiet"),
                )
            }
        }
    }
}

private data class Question(val question: String, val answer: String)

private val questions = listOf(
    Question(
        "Will the update be accurate?",
        "It is built from your Linear tickets and GitHub pull requests from the last day. " +
            "Each person can approve, edit or skip their update before it posts.",
    ),
    Question(
        "What does Claude see?",
        "Your Linear and GitHub activity for the standup window. On the Free plan it runs on our " +
            "Anthropic key. On Pro and Business it runs on your own key.",
    ),
    Question(
        "How much does it cost?",
        "Free for up to 5 members. Pro is $15 a month billed annually, or $19 monthly, with unlimited members " +
            "and standups. Business is $65 a month billed annually.",
    ),
    Question(
        "How long does setup take?",
        "Five steps from install to your first standup. The last step posts a real one so you can see it.",
    ),
    Question(
        "Can I choose when it posts?",
        "Yes. Pick the team, the Slack channel, the days and the time, and skip holidays.",
    ),
    Question(
        "Where is my data stored?",
        "In Sydney. Tokens are encrypted with AES GCM.",
    ),
    Question(
        "Can someone skip a day?",
        "Yes. Anyone can skip their own update, and nothing goes out for them.",
    ),
    Question(
        "What does the AI cost me?",
        "Every Anthropic call is tracked down to the cent per workspace. " +
            "On Pro and Business you bring your own key, so the spend sits on your account.",
    ),
)

@Composable
private fun FaqSection() {
    Section(attrs = { classes("mk-section", "mk-band", "mk-anchor"); attr("id", "faq") }) {
        Div(attrs = { classes("mk-wrap") }) {
            SectionHead(title = "Questions, answered.", line = "Anything else, write to us on the contact page.")
            Div(attrs = { classes("mk-faq") }) {
                questions.forEach { item ->
                    Fold(title = item.question) { P(attrs = { classes("mk-body") }) { Text(item.answer) } }
                }
            }
        }
    }
}

@Composable
private fun ClosingSection() {
    Section(attrs = { classes("mk-closing") }) {
        Div(attrs = { classes("mk-wrap") }) {
            Reveal(modifier = Modifier.classNames("mk-closing-inner")) {
                H2(attrs = { classes("mk-h2") }) { Text("Your next standup writes itself.") }
                P(attrs = { classes("mk-lede") }) { Text("Free for 5 people. No card needed.") }
                Div(attrs = { classes("mk-actions") }) {
                    Link(
                        path = INSTALL_PATH,
                        modifier = Modifier.classNames("mk-btn", "mk-btn--invert"),
                    ) { ArrowLabel(INSTALL_LABEL) }
                }
            }
        }
    }
}
